<template>
  <v-container class="container--large faq padding-left-right">
    <v-row class="pt-6 mb-12" align="center">
      <navigation-breadcrumb :items="items" />
    </v-row>
    <header-three title="FAQs" />
    <v-row align="center" justify="center" class="mt-8 faq__choices mb-8">
      <v-col cols="12" md="3" class="">
        <div class="input__wrapper">
          <search
            :searchBtn="true"
            :totalResult="searchTotalResult"
            :result="searchResult"
            placeholder="Search"
            @search="search"
            :chipResult="chipResult"
            :isHavingResultData="isHavingResultData"
          />
        </div>
      </v-col>
      <v-col class=""> </v-col>
    </v-row>
    <v-row class="mb-4">
      <v-col cols="12" md="8" sm="7">
        <div v-if="!loadingStates.faq">
          <template v-if="faqs.length < 1">
            <v-card class="mb-6 card-root pa-4" elevation="0">
              <div class="no-items">
                No items were found matching your search request. Please try
                another search.
              </div>
            </v-card>
          </template>
          <v-expansion-panels v-if="faqs" v-model="openedPanel" multiple>
            <div
              v-if="searchText && faqs.length > 0"
              class="faq__search-result"
            >
              <h2 v-if="faqs.length > 0" class="faq__title faq__search-result">
                Search results containing "{{ searchText }}"
              </h2>
            </div>
            <v-expansion-panel
              v-for="(item, i) in faqs"
              :key="i"
              class="faq__panel-main"
              :class="{ 'first-panel': i == 0 }"
            >
              <v-expansion-panel-header @click="handleClick(i, item.id, item.question)">
                <h2 class="faq__title mb-0">{{ item.question }}</h2>
                <template v-slot:actions>
                  <v-icon color="primary"> mdi-chevron-down </v-icon>
                </template>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="faq__content px-0 pt-0">
                <p v-html="item.answers"></p>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
        <v-row v-else align="center" justify="center" class="faq_list mb-8 pa-2">
          <v-col cols="12">
            <v-skeleton-loader
              v-for="i in 10"
              :key="i"
              type="text"
              loading
              class="search-result-text-loader"
            />
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" md="4" sm="5">
        <card-seven />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { FaqsApi } from '@gslc/models';
import Search from '../components/inputs/search.vue';
import HeaderThree from '../components/typography/header-title-3.vue';
import CardSeven from '../components/cards/card-7.vue';
import NavigationBreadcrumb from '../components/NavigationBreadcrumb.vue';
import { openApiConfig } from '../api/config';
import { SEGMENT_EVENTS } from '../constants';
import { mapGetters } from 'vuex';

const config = openApiConfig();
const faqsApi = new FaqsApi(config);
export default {
  components: {
    Search,
    HeaderThree,
    CardSeven,
    NavigationBreadcrumb,
  },
  data: () => ({
    items: [
      {
        text: 'Learning Center',
        disabled: false,
        to: '/',
      },
      {
        text: 'FAQs',
        disabled: true,
        to: '/faq',
      },
    ],
    openedPanel: [],
    faqs: [],
    searchResult: [],
    searchTotalResult: 0,
    faqsAll: [],
    loadingStates: {
      faq: false,
    },
    chipResult: false,
    searchText: '',
    isHavingResultData: false,
  }),
  methods: {
    handleClick(index, contentId, contentTitle) {
      if (this.openedPanel.includes(index)) {
        window.analytics.track(SEGMENT_EVENTS.COLLAPSE_FAQ_ITEM, {
          contentTitle,
          contentId,
          contentType: 'FAQ',
        });
      } else {
        window.analytics.track(SEGMENT_EVENTS.EXPAND_FAQ_ITEM, {
          contentTitle,
          contentId,
          contentType: 'FAQ',
        });
      }
    },
    async fetchFaqs() {
      try {
        this.loadingStates.faq = true;
        this.openedPanel = [];
        const accessToken = await this.$auth.getTokenSilently();
        const faqs = await faqsApi
          .getFaqs({ headers: { Authorization: `Bearer ${accessToken}` } })
          .catch(e => console.error(e));
        this.faqsAll = faqs?.data?.data ?? [];
        this.faqs = this.faqsAll;
        this.searchTotalResult = this.faqsAll.length;
      } catch (error) {
        console.log(error);
      } finally {
        this.loadingStates.faq = false;
      }
    },
    async search(text) {
      try {
        this.isHavingResultData = false;
        this.searchResult = [];
        this.searchText = text;
        this.openedPanel = [];
        const searchText = text?.toLowerCase() ?? '';
        const searchResult = this.faqsAll
          .filter(f => f.question.toLowerCase().indexOf(searchText) > -1)
          .sort((a, b) =>
            this.sortSearchResult(a.question, b.question, searchText)
          );
        if (text) {
          let answersSearched = this.faqsAll
            .filter(f => f?.answers?.toLowerCase().indexOf(searchText) > -1)
            .sort((a, b) =>
              this.sortSearchResult(a.answers, b.answers, searchText)
            );
          searchResult.push(...answersSearched);
        }
        const searchResultUnique = [...new Set(searchResult)];
        this.searchResult = searchResultUnique;
        this.searchTotalResult = this.searchResult.length;
        this.faqs = searchResultUnique;
        this.chipResult = !this.chipResult;
        if (text) {
          window.analytics.track(SEGMENT_EVENTS.SEARCH_FAQ, {
            searchTerm: String(text).toLocaleLowerCase(),
            contentType: 'FAQ',
          });
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.isHavingResultData = true;
      }
    },
    sortSearchResult(prevQuestion, nextQuestion, searchText) {
      const prevQuestionLetterIndex = prevQuestion
        .toLowerCase()
        .indexOf(searchText);
      const nextQuestionLetterIndex = nextQuestion
        .toLowerCase()
        .indexOf(searchText);
      return prevQuestionLetterIndex - nextQuestionLetterIndex;
    },
  },
  created() {
    this.fetchFaqs();
  },
};
</script>

<style scoped>
.faq .faq__choices {
  background-color: #fff;
  box-shadow: 0px 6px 18px rgba(0, 0, 0, 0.06);
  border-radius: 4px;
  margin: -1px;
}
.faq .input__wrapper {
  height: 66px;
  padding-top: 5px;
  padding-left: 5px;
}
.faq .faq__content {
  background-color: #fff;
}
.faq .faq__content >>> .v-expansion-panel-content__wrap {
  padding: 0 80px 16px 25px;
}

.faq .faq__content >>> .v-expansion-panel-content__wrap ul {
  list-style: none;
  padding-left: 15px;
}
.faq .faq__content >>> .v-expansion-panel-content__wrap * {
  position: relative;
  font-weight: normal;
  font-size: 14px;
  line-height: 17px;
  letter-spacing: 0.1px;
  color: #424540;
  margin-top: 8px;
}

.faq .faq__content >>> .v-expansion-panel-content__wrap ul > li::before {
  position: absolute;
  width: 6px;
  height: 6px;
  content: '';
  background-color: var(--v-primary-base);
  border-radius: 50%;
  left: -15px;
  top: 4px;
}

.faq .faq__panel-main:first-child {
  border: 0;
}
ul.alphanumeric {
  list-style: none;
}
ul.alphanumeric li {
  float: left;
  font-style: normal;
  font-weight: bold;
  font-size: 20px;
  line-height: 24px;
  text-align: center;
  letter-spacing: 0.1px;
  text-transform: capitalize;
  color: var(--v-primary-base);
  border-right: 1px solid #ccd3de;
  padding-left: 10px;
  padding-right: 10px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}
ul.alphanumeric li:last-child {
  border: 0;
}
.answers {
  padding-top: 10px;
}
.answers >>> ul {
  list-style: none;
}
.answers >>> ul li::before {
  content: '\2022';
  color: var(--v-primary-base);
  font-weight: bold;
  font-size: 2rem;
  line-height: 0.5rem;
  display: inline-block;
  width: 1rem;
  margin-left: -1.5rem;
  position: relative;
  top: 5px;
}
.faq,
.faq .v-expansion-panel::before,
.faq >>> .v-expansion-panel {
  box-shadow: none !important;
}
.faq__content {
  background: #ffffff;
  border-radius: 4px;
  margin: 0 !important;
}
.faq >>> .v-expansion-panel-header > h1.faq__index {
  font-weight: bold;
  font-size: 32px;
  line-height: 39px;
  letter-spacing: 0.1px;
  color: #234025;
}
.faq .faq__title {
  font-style: normal;
  font-weight: bold;
  font-size: 15px;
  line-height: 18px;
  letter-spacing: 0.1px;
  color: #234025;
}
.faq .faq__search-result {
  font-size: 20px;
  line-height: 24px;
  letter-spacing: 0.1px;
  color: #234025;
  padding-bottom: 16px;
  background-color: #fff;
  text-align: left;
  width: 100%;
  padding: 20px;
}
.faq .v-expansion-panel {
  margin-top: 0 !important;
}
.faq .v-expansion-panels .faq__panel-main.first-panel:after {
  border: 0 !important;
}
.expansion-panel-content {
  margin-top: 35px;
}
.no-items {
  font-size: 0.875rem;
  color: #424540bd;
  font-weight: 400;
}
</style>
<style lang="scss" scoped>
.faq_list {
  background-color: #fff;
  box-shadow: 0px 0.375rem 1.125rem rgba(0, 0, 0, 0.06);
  border-radius: 0.25rem;
  margin: -0.0625rem;
}
.search-result-text-loader {
  margin-bottom: 30px;
}
</style>
